import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ContentCopy, InfoOutlined, KeyboardArrowUp } from '@mui/icons-material';
import { Grid, IconButton, Box, Tooltip } from '@mui/material';
import { getOrderDetails } from 'api/orderManagement';
import { LoadingSkeleton, Toast } from 'components';
import { formatSubmissionDate, formatSubmissionTime, formatCurrency } from 'utils';
import {
  PageContainer,
  ContentWrapper,
  HeaderSection,
  OrderNumber,
  HeaderTitle,
  TitleText,
  JumpToSection,
  JumpToTitle,
  JumpToButton,
  InfoSection,
  InfoLabel,
  InfoValue,
  MonospaceValue,
  OrderDetailsSection,
  SectionTitle,
  CopyButton,
  LocationCard,
  OrderDetailsCard,
  PayloadCard,
  PayloadCardHeader,
  PayloadCardContent,
  CollapsedPayloadCard,
  PayloadNumber,
  CopyAllButton,
  StickyJumpToCard,
  JumpToButtonsScroll,
  JsonDisplayBox,
} from './OrderRawPayloadPageStyles';
import { StyledChip } from './OrderDetailsDrawerStyles'

import { translateOrderStatus } from './OrderDetailsDrawer';
import { OrderDisplayStatus, statusConfig } from './OrderManagement';
import { PaymentProviderIncluded } from 'api/orderManagement/types';

const parseJsonPayload = (payload: string | Record<string, any> | null | undefined): Record<string, any> => {
  if (!payload) return {};
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch (e) {
      console.error('Error parsing JSON payload:', e);
      return {};
    }
  }
  return payload;
};

// Unique toast IDs to prevent duplicates
const TOAST_IDS = {
  INVALID_ORDER: 'invalid-order-id-toast',
  NO_PAYLOAD: 'no-payload-data-toast',
  FETCH_ERROR: 'fetch-order-payloads-error-toast'
};

// Tooltip mapping for each payload type
const payloadTooltips: Record<string, string> = {
  'toos-payload': 'Raw data from third-party platform.',
  'chowly-payload': "Standardized format for Chowly ingestion.",
  'mapping-payload': 'Links items/modifiers to POS IDs.',
  'pos-ticket-payload': 'Data sent to the POS system.',
  'finalizer-payload': 'Downstream events, confirmations, and triggers.',
};

const OrderRawPayloadPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchOrderPayloads = async () => {
      if (!orderId || !orderId.trim() || isNaN(Number(orderId))) {
        if (isMounted.current) {
          navigate('/order-management');
          setTimeout(() => {
            Toast.error(
              { message: 'Invalid order ID' },
              { autoClose: 5000, toastId: TOAST_IDS.INVALID_ORDER }
            );
          }, 100);
        }
        return;
      }

      try {
        setIsLoading(true);
        const response = await getOrderDetails(orderId);
        const { data, included } = response;

        if (!isMounted.current) return;

        if (!data.attributes.toos_payload && !data.attributes.chowly_payload &&
            !data.attributes.mapping_payload && !data.attributes.pos_payload &&
            !data.attributes.complete_payload) {
          navigate('/order-management');
          setTimeout(() => {
            Toast.error(
              { message: 'No payload data available for this order' },
              { autoClose: 5000, toastId: TOAST_IDS.NO_PAYLOAD }
            );
          }, 100);
          return;
        }

        if (isMounted.current) {
          setOrder({ data, included });
        }
      } catch (err) {
        console.error('Error fetching order payloads:', err);
        if (isMounted.current) {
          navigate('/order-management');
          setTimeout(() => {
            Toast.error(
              { message: 'Failed to load order payloads. The order may not exist or you may not have permission to view it.' },
              { autoClose: 5000, toastId: TOAST_IDS.FETCH_ERROR }
            );
          }, 100);
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    };

    fetchOrderPayloads();
  }, [orderId, navigate]);

  let payloadSections: Array<{ id: string; title: string; number: string; data: any }>; 
  if (!order) {
    payloadSections = [];
  } else {
    const {
      attributes: {
        toos_payload,
        chowly_payload,
        mapping_payload,
        pos_payload,
        complete_payload,
      },
    } = order.data;
    payloadSections = [
      { id: 'toos-payload', title: 'TooS Payload', number: '1', data: parseJsonPayload(toos_payload) },
      { id: 'chowly-payload', title: 'Chowly Payload', number: '2', data: parseJsonPayload(chowly_payload) },
      { id: 'mapping-payload', title: 'Mapping Payload', number: '3', data: parseJsonPayload(mapping_payload) },
      { id: 'pos-ticket-payload', title: 'POS Ticket Payload', number: '4', data: parseJsonPayload(pos_payload) },
      { id: 'finalizer-payload', title: 'Finalizer Payload', number: '5', data: parseJsonPayload(complete_payload) },
    ];
  }

  const [collapsed, setCollapsed] = useState(() => Array(payloadSections.length).fill(false));
  const [copied, setCopied] = useState(() => Array(payloadSections.length).fill(false));

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    const stickyBar = document.getElementById('sticky-jump-to-bar');
    if (element) {
      const rect = element.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const stickyBarHeight = stickyBar ? stickyBar.offsetHeight : 0;
      window.scrollTo({
        top: rect.top + scrollTop - stickyBarHeight - 8, // 8px extra spacing
        behavior: 'smooth',
      });
    }
  };

  const handleCopyAll = async () => {
    if (!order) return;

    try {
      const payloads = {
        toos_raw_payload: parseJsonPayload(order.data.attributes.toos_payload),
        chowly_payload: parseJsonPayload(order.data.attributes.chowly_payload),
        mapping_payload: parseJsonPayload(order.data.attributes.mapping_payload),
        pos_ticket_payload: parseJsonPayload(order.data.attributes.pos_payload),
        complete_finalizer_payload: parseJsonPayload(order.data.attributes.complete_payload),
      };
      await navigator.clipboard.writeText(JSON.stringify(payloads, null, 2));
      Toast.success({ message: 'All payloads copied to clipboard' }, { autoClose: 3000 });
    } catch (err) {
      Toast.error({ message: 'Failed to copy payloads' }, { autoClose: 3000 });
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <ContentWrapper>
          <HeaderSection>
            <LoadingSkeleton width={200} height={24} style={{ marginBottom: '16px' }} />
            <LoadingSkeleton width={400} height={32} style={{ marginBottom: '24px' }} />
          </HeaderSection>
          {[...Array(5)].map((_, idx) => (
            <div key={idx} style={{ width: '100%', marginBottom: '32px' }}>
              <LoadingSkeleton width={200} height={24} style={{ marginBottom: '16px' }} />
              <LoadingSkeleton width="100%" height={200} style={{ borderRadius: '8px' }} />
            </div>
          ))}
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (!order) return null;

  const {
    attributes: {
      order_platform_order_id,
      platform_name,
      submission_time,
      completed_at,
      total,
      payment_type,
      future,
      customer_first_name,
      customer_last_name,
      location_name,
      partner_name,
      order_status,
      placed_at,
    },
    id: chowlyOrderId,
  } = order.data;

  const { included } = order
  const payment = included?.find((inc: any) => inc.type === 'payment_provider') as PaymentProviderIncluded | undefined
  const paymentProvider = payment?.attributes?.kind?.toLocaleLowerCase() || ''


  return (
    <PageContainer>
      <ContentWrapper>
        <HeaderSection>
          <OrderNumber variant="body2">
            Order #{chowlyOrderId}
          </OrderNumber>
          <HeaderTitle>
            <TitleText variant="h4">Order Payload Details</TitleText>
            <StyledChip
              label={translateOrderStatus(order_status)}
              icon={statusConfig[translateOrderStatus(order_status) as OrderDisplayStatus].icon}
              status={translateOrderStatus(order_status) as OrderDisplayStatus}
              size='medium'
              sx={{ flexShrink: 0 }}
            />
          </HeaderTitle>
        </HeaderSection>

        {/* Jump To Card */}
        <StickyJumpToCard id="sticky-jump-to-bar">
          <JumpToSection>
            <JumpToTitle variant="body2">Jump To</JumpToTitle>
            <JumpToButtonsScroll>
              {payloadSections.map((section) => (
                <JumpToButton
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  size="small"
                >
                  <PayloadNumber>{section.number}</PayloadNumber> {section.title}
                </JumpToButton>
              ))}
              <CopyAllButton
                onClick={handleCopyAll}
                size="small"
                startIcon={<ContentCopy />}
              >
                Copy All
              </CopyAllButton>
            </JumpToButtonsScroll>
          </JumpToSection>
        </StickyJumpToCard>

        {/* Location Card */}
        <LocationCard>
          <InfoSection>
            <InfoLabel variant="body2">Location</InfoLabel>
            <InfoValue variant="body1">
              {location_name || partner_name}
            </InfoValue>
          </InfoSection>
          <Grid container spacing={2} sx={{ marginTop: 2 }}>
            <Grid item xs={6}>
              <InfoLabel variant="body2">Chowly Order ID</InfoLabel>
              <MonospaceValue variant="body1">{chowlyOrderId}</MonospaceValue>
            </Grid>
            <Grid item xs={6}>
              <InfoLabel variant="body2">TooS Order ID</InfoLabel>
              <MonospaceValue variant="body1">{order_platform_order_id}</MonospaceValue>
            </Grid>
          </Grid>
        </LocationCard>

        {/* Order Details Card */}
        <OrderDetailsCard>
          <OrderDetailsSection>
            <SectionTitle variant="h6">Order Details</SectionTitle>
            <Grid container spacing={4}>
              <Grid item xs={4}>
                <InfoLabel variant="body2">Customer Name</InfoLabel>
                <InfoValue variant="body1">
                  {`${customer_first_name} ${customer_last_name}`}
                </InfoValue>
              </Grid>
              <Grid item xs={4}>
                <InfoLabel variant="body2">Order Total</InfoLabel>
                <InfoValue variant="body1">{formatCurrency(total || 0)}</InfoValue>
              </Grid>
              <Grid item xs={4}>
                <InfoLabel variant="body2">Future?</InfoLabel>
                <InfoValue variant="body1">{future === 'true' ? 'True' : 'False'}</InfoValue>
              </Grid>
              <Grid item xs={4}>
                <InfoLabel variant="body2">Created At</InfoLabel>
                <InfoValue variant="body1">
                  {submission_time ? `${formatSubmissionDate(submission_time)} ${formatSubmissionTime(submission_time)}` : 'N/A'}
                </InfoValue>
              </Grid>
              <Grid item xs={4}>
                <InfoLabel variant="body2">Placed At</InfoLabel>
                <InfoValue variant="body1">
                  {placed_at ? `${formatSubmissionDate(placed_at)} ${formatSubmissionTime(placed_at)}` : 'N/A'}
                </InfoValue>
              </Grid>
              <Grid item xs={4}>
                <InfoLabel variant="body2">Payment Provider</InfoLabel>
                <InfoValue variant="body1" data-testid="payment-provider">{paymentProvider}</InfoValue>
              </Grid>
              <Grid item xs={4}>
                <InfoLabel variant="body2">Order Platform</InfoLabel>
                <InfoValue variant="body1">{platform_name}</InfoValue>
              </Grid>
              <Grid item xs={4}>
                <InfoLabel variant="body2">Completed At</InfoLabel>
                <InfoValue variant="body1">
                  {completed_at ? `${formatSubmissionDate(completed_at)} ${formatSubmissionTime(completed_at)}` : 'N/A'}
                </InfoValue>
              </Grid>
              <Grid item xs={4}>
                <InfoLabel variant="body2">Payment Type</InfoLabel>
                <InfoValue variant="body1">{payment_type}</InfoValue>
              </Grid>
            </Grid>
          </OrderDetailsSection>
        </OrderDetailsCard>

        {/* Payload Cards */}
        {payloadSections.map((section, idx) => {
          const CardComponent = collapsed[idx] ? CollapsedPayloadCard : PayloadCard;
          return (
            <CardComponent key={section.id} id={section.id}>
              <PayloadCardHeader
                sx={{ cursor: 'pointer' }}
                onClick={() => setCollapsed(prev => {
                  const copy = [...prev];
                  copy[idx] = !copy[idx];
                  return copy;
                })}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <PayloadNumber>
                    {section.number}
                  </PayloadNumber>
                  <TitleText variant="h6">{section.title}</TitleText>
                  <Tooltip title={payloadTooltips[section.id]} arrow>
                    <IconButton size="small" sx={{ color: '#1B51A4' }} aria-label="info" onClick={e => e.stopPropagation()}>
                      <InfoOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    setCollapsed(prev => {
                      const copy = [...prev];
                      copy[idx] = !copy[idx];
                      return copy;
                    });
                  }}
                >
                  <KeyboardArrowUp
                    sx={{
                      transform: collapsed[idx] ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                    }}
                  />
                </IconButton>
              </PayloadCardHeader>
              {!collapsed[idx] && (
                <PayloadCardContent>
                  <Box mt={2} mb={2}>
                    <CopyButton
                      variant="outlined"
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(JSON.stringify(section.data, null, 2));
                          setCopied(prev => {
                            const copyArr = [...prev];
                            copyArr[idx] = true;
                            return copyArr;
                          });
                          setTimeout(() => {
                            setCopied(prev => {
                              const copyArr = [...prev];
                              copyArr[idx] = false;
                              return copyArr;
                            });
                          }, 2000);
                        } catch (err) {
                          console.error('Error copying payload:', err);
                        }
                      }}
                    >
                      {copied[idx] ? 'Copied!' : 'Copy'}
                    </CopyButton>
                  </Box>
                  <JsonDisplayBox>
                    <pre>{JSON.stringify(section.data, null, 2)}</pre>
                  </JsonDisplayBox>
                </PayloadCardContent>
              )}
            </CardComponent>
          );
        })}
      </ContentWrapper>
    </PageContainer>
  );
};

export default OrderRawPayloadPage; 